import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { APP_DISPLAY_NAME, JCONSULT_LOGO_WHITE } from '../../config/brandingAssets';
import { PLATFORM_CONTACT } from '../../config/publicPlatformContact';
import { PUBLIC_HOME_NAV_ITEMS } from '../../config/publicHomeNav';
import { usePublicBranding } from '../../hooks/usePublicBranding';
import PublicSocialLinks from './PublicSocialLinks';
import '../../styles/publicSiteFooter.css';

const FOOTER_INFO_LINKS = [
  { label: "Conditions d'utilisation", to: '/legal/cgu', legalKey: 'cgu' as const },
  { label: 'Politique de confidentialité', to: '/legal/confidentialite', legalKey: 'confidentialite' as const },
  { label: 'Connexion', to: '/login' },
  { label: 'Créer un compte', to: '/login?mode=register' },
] as const;

type Props = {
  className?: string;
  maxWidthClass?: 'max-w-4xl' | 'max-w-6xl';
  showHomeLink?: boolean;
  showLoginLink?: boolean;
  activeLegal?: 'cgu' | 'confidentialite';
};

function FooterListItem({
  children,
  to,
  href,
  active,
}: {
  children: ReactNode;
  to?: string;
  href?: string;
  active?: boolean;
}) {
  const className = `text-sm transition ${
    active ? 'font-semibold text-white' : 'text-white/65 hover:text-white'
  }`;

  if (to) {
    return (
      <Link to={to} className={className}>
        {children}
      </Link>
    );
  }

  return (
    <a href={href} className={className}>
      {children}
    </a>
  );
}

function FooterLinkList({
  title,
  children,
  align = 'start',
  hint,
}: {
  title: string;
  children: ReactNode;
  align?: 'start' | 'center' | 'end';
  hint?: string;
}) {
  const containerAlign =
    align === 'center'
      ? 'w-full lg:mx-auto lg:w-max lg:max-w-full'
      : align === 'end'
        ? 'w-full lg:ml-auto lg:w-max lg:max-w-full'
        : 'w-full';

  return (
    <div className={containerAlign}>
      <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/40">
        {title}
      </p>
      <ul className="mt-4 list-disc space-y-2 pl-4 text-left marker:text-white/35">
        {children}
      </ul>
      {hint ? <p className="mt-4 text-xs leading-relaxed text-white/40">{hint}</p> : null}
    </div>
  );
}

export default function PublicSiteFooter({
  className = '',
  maxWidthClass = 'max-w-6xl',
  showHomeLink = false,
  showLoginLink = true,
  activeLegal,
}: Props) {
  const branding = usePublicBranding();
  const supportMailto = `mailto:${branding.supportEmail}?subject=${encodeURIComponent(
    'Assistance JCONSULT MY Academia',
  )}`;
  const supportPhoneHref = branding.supportPhone
    ? `tel:${branding.supportPhone.replace(/\s/g, '')}`
    : '';

  const infoLinks = FOOTER_INFO_LINKS.filter((item) => {
    if (item.to === '/login' || item.to === '/login?mode=register') {
      return showLoginLink;
    }
    return true;
  });

  return (
    <footer className={`public-site-footer relative overflow-hidden bg-slate-950 text-white/70 ${className}`}>
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/70 to-transparent"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -left-24 top-0 h-72 w-72 rounded-full bg-primary/10 blur-3xl"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -right-24 bottom-0 h-64 w-64 rounded-full bg-primary/5 blur-3xl"
        aria-hidden
      />

      <div className={`relative z-[1] mx-auto ${maxWidthClass} px-4 py-12 sm:px-6 lg:py-14`}>
        <div className="grid grid-cols-1 gap-10 sm:gap-12 lg:grid-cols-12 lg:gap-8 xl:gap-10">
          <div className="flex w-full flex-col items-start lg:col-span-6">
            <Link to="/" className="inline-flex">
              <img
                src={JCONSULT_LOGO_WHITE}
                alt={APP_DISPLAY_NAME}
                className="h-16 w-auto max-w-[300px] object-contain opacity-95 transition hover:opacity-100 sm:h-[4.5rem] sm:max-w-[340px] lg:h-20 lg:max-w-[380px]"
              />
            </Link>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-white/55">
              Centre de formation professionnelle certifiante — inscriptions, suivi et certification à{' '}
              {PLATFORM_CONTACT.addressLine1}.
            </p>
            <a
              href={supportMailto}
              className="mt-4 inline-block text-sm font-medium text-white/70 transition hover:text-white"
            >
              {branding.supportEmail}
            </a>
            {supportPhoneHref ? (
              <a
                href={supportPhoneHref}
                className="mt-2 block text-sm font-medium text-white/70 transition hover:text-white"
              >
                {branding.supportPhone}
              </a>
            ) : null}
            {branding.socialLinks.length > 0 ? (
              <PublicSocialLinks
                layout="footer"
                links={branding.socialLinks}
                className="mt-8 w-full"
              />
            ) : null}
          </div>

          <div className="w-full lg:col-span-2">
            <FooterLinkList title="Parcourir" align="center">
              {showHomeLink ? (
                <li>
                  <FooterListItem to="/">Accueil</FooterListItem>
                </li>
              ) : null}
              {PUBLIC_HOME_NAV_ITEMS.map((item) => (
                <li key={item.id}>
                  <FooterListItem to={`/${item.hash}`}>{item.label}</FooterListItem>
                </li>
              ))}
            </FooterLinkList>
          </div>

          <div className="w-full lg:col-span-2">
            <FooterLinkList title="Informations" align="end">
              {infoLinks.map((item) => (
                <li key={item.to}>
                  <FooterListItem
                    to={item.to}
                    active={'legalKey' in item && item.legalKey === activeLegal}
                  >
                    {item.label}
                  </FooterListItem>
                </li>
              ))}
            </FooterLinkList>
          </div>

          <div className="w-full lg:col-span-2">
            <FooterLinkList title="Vacations" align="end" hint={PLATFORM_CONTACT.vacationSlotsHint}>
              {PLATFORM_CONTACT.vacationSlots.map((slot) => (
                <li key={slot}>
                  <span className="text-sm tabular-nums text-white/65">{slot}</span>
                </li>
              ))}
            </FooterLinkList>
          </div>
        </div>

        <div className="mt-12 flex flex-col gap-3 border-t border-white/10 pt-8 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-white/40">
            © {new Date().getFullYear()} {PLATFORM_CONTACT.legalName} — {APP_DISPLAY_NAME}
          </p>
          <p className="text-xs text-white/35">
            {PLATFORM_CONTACT.addressLine1}, {PLATFORM_CONTACT.addressLine2}
          </p>
        </div>
      </div>
    </footer>
  );
}
